import { useState } from 'react';
import { Users, ChevronRight } from 'lucide-react';
import joinBackground from '../assets/join-background.jpg';

export default function Join() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    year: '',
    interest: '',
    additional: ''
  });

  const handleSubmit = () => {
    if (formData.name && formData.email && formData.year && formData.interest) {
      const mailtoLink = `mailto:maneeboard@gmail.com?subject=Interest Form from ${encodeURIComponent(formData.name)}&body=${encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\nYear: ${formData.year}\n\nWhy I'm Interested:\n${formData.interest}\n\nAnything Else:\n${formData.additional || 'N/A'}`
      )}`;
      window.location.href = mailtoLink;
      setFormData({ name: '', email: '', year: '', interest: '', additional: '' });
    } else {
      alert('Please fill in all required fields.');
    }
  };

  return (
    <section className="pt-24 py-16 px-4 sm:px-6 lg:px-8 bg-michigan-blue min-h-screen relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: `url(${joinBackground})` }}
      />
      <div className="absolute inset-0 bg-michigan-blue opacity-70" />
      
      {/* Content */}
      <div className="max-w-4xl mx-auto text-center w-full relative z-10">
        <Users 
          className="w-16 h-16 text-michigan-maize mx-auto mb-6"
          data-aos="zoom-in"
        />
        <h2 
          className="text-4xl font-bold text-white mb-6"
          data-aos="fade-down"
          data-aos-delay="100"
        >
          Join Us
        </h2>
        <p 
          className="text-xl text-blue-100 mb-8"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          Whether you're passionate about clean energy, curious about nuclear technology, or interested in energy policy, there's a place for you in MANE. All majors and experience levels are welcome!
        </p>
        
        {/* Membership Benefits */}
        <div 
          className="bg-white rounded-xl p-8 shadow-2xl mb-12"
          data-aos="fade-up"
          data-aos-delay="300"
        >
          <h3 className="text-2xl font-semibold text-michigan-blue mb-4">Membership Benefits</h3>
          <div className="grid md:grid-cols-2 gap-4 text-left">
            <div className="flex items-start space-x-3">
              <ChevronRight className="w-5 h-5 text-michigan-maize mt-1 flex-shrink-0" />
              <span className="text-gray-700">Access to exclusive workshops and seminars</span>
            </div>
            <div className="flex items-start space-x-3">
              <ChevronRight className="w-5 h-5 text-michigan-maize mt-1 flex-shrink-0" />
              <span className="text-gray-700">Networking with industry professionals</span>
            </div>
            <div className="flex items-start space-x-3">
              <ChevronRight className="w-5 h-5 text-michigan-maize mt-1 flex-shrink-0" />
              <span className="text-gray-700">Nuclear facility tours</span>
            </div>
            <div className="flex items-start space-x-3">
              <ChevronRight className="w-5 h-5 text-michigan-maize mt-1 flex-shrink-0" />
              <span className="text-gray-700">Career development opportunities</span>
            </div>
          </div>
        </div>

        {/* Interest Form */}
        <div 
          className="bg-white rounded-xl p-8 shadow-2xl text-left"
          data-aos="fade-up"
          data-aos-delay="400"
        >
          <h3 className="text-2xl font-semibold text-michigan-blue mb-6 text-center">Express Your Interest</h3>
          
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-michigan-blue focus:border-transparent"
                placeholder="Your full name"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-michigan-blue focus:border-transparent"
                placeholder="your.email@umich.edu"
              />
            </div>

            {/* Year */}
            <div>
              <label htmlFor="year" className="block text-gray-700 font-medium mb-2">
                Year <span className="text-red-500">*</span>
              </label>
              <select
                id="year"
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-michigan-blue focus:border-transparent"
              >
                <option value="">Select your year</option>
                <option value="Freshman">Freshman</option>
                <option value="Sophomore">Sophomore</option>
                <option value="Junior">Junior</option>
                <option value="Senior">Senior</option>
                <option value="Graduate">Graduate Student</option>
              </select>
            </div>

            {/* Why Interested */}
            <div>
              <label htmlFor="interest" className="block text-gray-700 font-medium mb-2">
                Why are you interested in MANE? <span className="text-red-500">*</span>
              </label>
              <textarea
                id="interest"
                value={formData.interest}
                onChange={(e) => setFormData({...formData, interest: e.target.value})}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-michigan-blue focus:border-transparent"
                placeholder="Tell us what draws you to nuclear energy and MANE..."
              />
            </div>

            {/* Anything Else */}
            <div>
              <label htmlFor="additional" className="block text-gray-700 font-medium mb-2">
                Anything else you'd like us to know?
              </label>
              <textarea
                id="additional"
                value={formData.additional}
                onChange={(e) => setFormData({...formData, additional: e.target.value})}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-michigan-blue focus:border-transparent"
                placeholder="Optional: Questions, special interests, availability, etc."
              />
            </div>

            {/* Submit Button */}
            <button 
              onClick={handleSubmit}
              className="w-full bg-michigan-maize text-michigan-blue py-3 rounded-lg font-bold hover:bg-yellow-400 transition shadow-lg"
            >
              Submit Interest Form
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
